import React, { useState } from "react";
import { Box, Tabs, Tab, Typography, Paper } from "@mui/material";
import { PageLayout } from "../../ui-components/PageLayout";
import ClientRepeatsTab from "./ClientRepeatsTab";
import SoiReferrersTab from "./SoiReferrersTab";
import VendorReferrersTab from "./VendorReferrersTab";
import AgentReferrersTab from "./AgentReferrersTab";
import ClientRepeatsAndReferrersTab from "./ClientRepeatsAndReferrersTab";
import AgentsReferreredTab from "./AgentsReferreredTab";
import DuplicateCandidateAlert from "../../components/DuplicateCandidateAlert";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { CoAgentFilterSelect } from "../../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../../hooks/usePageCoAgentFilter";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`repeats-referrers-tabpanel-${index}`}
      aria-labelledby={`repeats-referrers-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `repeats-referrers-tab-${index}`,
    "aria-controls": `repeats-referrers-tabpanel-${index}`,
  };
};

const RepeatsAndReferrers: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { currentUser: user } = useSelector((state: RootState) => state?.user);
  const isPro = user?.isPro || user?.isAdmin || false;
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];
  const isCoAgentFilterVisible =
    !hiddenFields?.some((field) => field.name === "coAgent");

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("repeatsAndReferrers", selectedCoAgentIds);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <PageLayout>
      <Box sx={{ mb: 6, ml: 1, mr: 1 }}>
        <Typography
          variant="h4"
          color="primary"
          component="h1"
          fontWeight="bold"
          sx={{ mb: 3 }}
        >
          Repeats and Referrers
        </Typography>

        <DuplicateCandidateAlert />

        {/* Page-level Co-Agent Filter (hidden when global filter is active or coAgent category is hidden) */}
        {!isGlobalFilterActive && isCoAgentFilterVisible && (
          <Box sx={{ mb: 3 }}>
            <CoAgentFilterSelect
              coAgents={coAgents}
              selectedCoAgentIds={effectiveCoAgentIds}
              onChange={setPageCoAgentIds}
              label="Filter by Team Members"
              size="medium"
            />
          </Box>
        )}

        <Paper elevation={2} sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Repeats and Referrers tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Client Repeats" {...a11yProps(0)} />
              <Tab label="SOI Referrers" {...a11yProps(1)} />
              <Tab label="Vendor Referrers" {...a11yProps(2)} />
              <Tab label="Agent Referrers" {...a11yProps(3)} />
              <Tab label="Client Repeats + Referrers" {...a11yProps(4)} />
              <Tab label="Agents Referred" {...a11yProps(5)} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <ClientRepeatsTab isPro={isPro} coAgentIds={effectiveCoAgentIds} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <SoiReferrersTab isPro={isPro} coAgentIds={effectiveCoAgentIds} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <VendorReferrersTab
              isPro={isPro}
              coAgentIds={effectiveCoAgentIds}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <AgentReferrersTab isPro={isPro} coAgentIds={effectiveCoAgentIds} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <ClientRepeatsAndReferrersTab
              isPro={isPro}
              coAgentIds={effectiveCoAgentIds}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <AgentsReferreredTab
              isPro={isPro}
              coAgentIds={effectiveCoAgentIds}
            />
          </TabPanel>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default RepeatsAndReferrers;
